import { Location } from '@angular/common';
import { Injectable } from '@angular/core';
import { APP_ROUTES } from '@cpd-app/core-module/constants/routes';
import { STORAGE_KEY } from '@cpd-app/core-module/constants/storage-key.const';
import {
  AuthDataService,
  CognitoAuthService,
  NavigationService
} from '@cpd-app/core-module/services';
import { MagicLinkLandingService } from '@cpd-app/core-module/services/auth/magic-link-landing.service';
import { PosthogService } from '@cpd-app/core-module/services/posthog/posthog.service';
import { Actions, createEffect, ofType, OnInitEffects } from '@ngrx/effects';
import { of } from 'rxjs';
import {
  catchError,
  map,
  switchMap,
  take,
  tap,
  withLatestFrom
} from 'rxjs/operators';
import * as AuthActions from '../auth/auth.actions';
import { AuthFacade } from '../auth/auth.facade';
import { LoaderFacade } from '../loader';
import { UserProfileActions } from '../user-profile';
import * as UserActions from '../user/user.actions';

import * as CognitoAuthActions from './cognito-auth.actions';
import { CognitoAuthFacade } from './cognito-auth.facade';
import { cognitoAuthFeatureKey } from './cognito-auth.state';

@Injectable()
export class CognitoAuthEffects implements OnInitEffects {
  public signinWithGoogle$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(CognitoAuthActions.signinWithGoogle),
      switchMap(() => {
        this.loaderFacade.showLoaderUntil(
          CognitoAuthActions.signInWithGoogleInProgress.type
        );
        /* delete current user profile before logged in */
        return [
          CognitoAuthActions.signInWithGoogleInProgress(),
          UserProfileActions.deleteUserProfile()
        ];
      })
    );
  });

  public signInWithGoogleInProgress$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(CognitoAuthActions.signInWithGoogleInProgress),
        tap(() => {
          this.cognitoAuthService.signInWithGoogle();
        })
      );
    },
    { dispatch: false }
  );

  public signinWithLinkedIn$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(CognitoAuthActions.signinWithLinkedIn),
      switchMap(() => {
        this.loaderFacade.showLoaderUntil(
          CognitoAuthActions.signInWithLinkedInInProgress.type
        );
        /* delete current user profile before logged in */
        return [
          CognitoAuthActions.signInWithLinkedInInProgress(),
          UserProfileActions.deleteUserProfile()
        ];
      })
      // No tap here, deliberately: signInWithLinkedInInProgress$ below is the
      // single caller, matching signinWithGoogle$. A tap in this position runs
      // once per action the switchMap emits (two of them), so together with
      // that effect one click fired signInWithLinkedIn() three times and
      // LinkedIn had to be dismissed repeatedly to get through.
    );
  });

  public signInWithLinkedInInProgress$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(CognitoAuthActions.signInWithLinkedInInProgress),
        tap(() => {
          this.cognitoAuthService.signInWithLinkedIn();
        })
      );
    },
    { dispatch: false }
  );

  public signInWithMagicLink$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(CognitoAuthActions.signInWithMagicLink),
      switchMap(({ email }) => {
        this.loaderFacade.showLoaderUntil(
          CognitoAuthActions.signInWithMagicLinkSuccess.type
        );

        return this.cognitoAuthService.signUpWithMagicLink(email).pipe(
          map((result) => {
            /* save session token to localStorage */
            const sessionToken =
              sessionStorage.getItem(STORAGE_KEY.TOKEN) || '';
            localStorage.setItem(`${STORAGE_KEY.TOKEN}_${email}`, sessionToken);
            return CognitoAuthActions.signInWithMagicLinkSuccess({
              result,
              email
            });
          }),
          catchError((error) =>
            of(CognitoAuthActions.signInWithMagicLinkFailure({ error }))
          )
        );
      })
    );
  });

  public signInWithMagicLinkInNewSession$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(CognitoAuthActions.signInWithMagicLinkInNewSession),
      switchMap(({ email, token }) => {
        // INFRA.7b: the landing path sends no email, so it must not note its
        // own address as the one the link was requested from.
        return this.cognitoAuthService.signInWithMagicLink(email, { noteRequest: false }).pipe(
          map(() => {
            /* save session token to sessionStorage */
            const sessionToken = localStorage.getItem(
              `${STORAGE_KEY.TOKEN}_${email}`
            );
            sessionStorage.setItem(STORAGE_KEY.TOKEN, sessionToken || '');
            this.authFacade.setTokenToStorage(sessionToken);
            /* delete session token from localStorage */
            localStorage.removeItem(`${STORAGE_KEY.TOKEN}_${email}`);

            return CognitoAuthActions.confirmSignInWithMagicLink({
              email,
              token
            });
          }),
          /*
          Without this, a signIn rejection is an unhandled effect error: no
          action is dispatched, so the loader shown for the magic-link
          confirmation stays up forever over a working page. The common
          rejection is UserAlreadyAuthenticatedException (a consumed link
          reloaded while signed in), which recovers by checking the existing
          session instead of failing.
          */
          catchError((error) =>
            of(
              error?.name === 'UserAlreadyAuthenticatedException'
                ? CognitoAuthActions.checkAuthenticationStatus()
                : CognitoAuthActions.signInWithMagicLinkFailure({ error })
            )
          )
        );
      })
    );
  });

  public confirmSignInWithMagicLink$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(CognitoAuthActions.confirmSignInWithMagicLink),
      map(({ token, email }) => {
        this.loaderFacade.showLoaderUntil(
          CognitoAuthActions.confirmSignInWithMagicLinkSuccess.type
        );

        return { token, email };
      }),
      switchMap(({ token, email }) => {
        return this.cognitoAuthService.confirmSignInWithMagicLink(token).pipe(
          map(() => CognitoAuthActions.confirmSignInWithMagicLinkSuccess()),
          catchError((error) => {
            /*
            AUTH.7. A used or expired link is no longer a one-second toast and
            a reload to the home page. The failure reaches the sign-in dialog,
            which says what happened and offers a new link.
            */
            if (error.name === 'NotAuthorizedException') {
              return of(
                CognitoAuthActions.confirmSignInWithMagicLinkFailure({
                  error
                })
              );
            }

            if (error.name === 'SignInException') {
              return of(
                CognitoAuthActions.signInWithMagicLinkInNewSession({
                  email,
                  token
                })
              );
            }

            /*
            Reload of an already-consumed link while still signed in: the
            challenge cannot be re-run, but the session is recoverable
            without it.
            */
            if (error.name === 'UserAlreadyAuthenticatedException') {
              return of(CognitoAuthActions.checkAuthenticationStatus());
            }

            return of(
              CognitoAuthActions.confirmSignInWithMagicLinkFailure({
                error
              })
            );
          })
        );
      })
    );
  });

  public confirmSignInWithMagicLinkSuccess$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(CognitoAuthActions.confirmSignInWithMagicLinkSuccess),
      map(() => {
        return CognitoAuthActions.checkAuthenticationStatus();
      })
    );
  });

  public checkAuthenticationStatus$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(CognitoAuthActions.checkAuthenticationStatus),
      switchMap(() => {
        this.navigationService.setDirectLink(window.location.pathname);

        return this.cognitoAuthService.getCurrentUser().pipe(
          map((user) => {
            const isAuthenticated = !!user;

            return CognitoAuthActions.checkAuthenticationStatusSuccess({
              isAuthenticated,
              user
            });
          }),
          catchError((error) => {
            return of(
              CognitoAuthActions.checkAuthenticationStatusFailure({ error })
            );
          })
        );
      })
    );
  });

  public checkAuthenticationStatusSuccess$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(CognitoAuthActions.checkAuthenticationStatusSuccess),
      withLatestFrom(this.authFacade.authToken$),
      map(([{ isAuthenticated, user }, authToken]) => {
        this.loaderFacade.showLoaderUntil(AuthActions.getSessionSuccess.type);

        const userToken = user
          ? localStorage.getItem(`${STORAGE_KEY.TOKEN}_${user.userId}`)
          : null;

        if (!isAuthenticated) {
          return AuthActions.initAuthState(); // init session without login
        }

        /*
         * THE LIVE SESSION IS THE MEMBER'S WORK. MERGE IT, NEVER REPLACE IT.
         *
         * This is where signing in used to wipe a member's surveys, on the
         * magic link and on Google and LinkedIn alike.
         *
         * The order of these branches used to be: restore the per-user token
         * if there is one, otherwise merge, otherwise mint. The first branch
         * fires for anybody who has signed in on this browser before, because
         * `saveTokenToStorage$` leaves a token under their user id, and it
         * overwrote sessionStorage with that OLD token and went straight to
         * `initAuthState`. `cognitoCallback` is the only thing that links the
         * session in hand to the account, so it never ran: everything answered
         * as a guest was left behind on a session nothing pointed at any more.
         *
         * On the magic link that is guaranteed rather than likely.
         * `signInWithMagicLinkInNewSession` has just restored the guest token
         * into sessionStorage under the member's email, so the work is sitting
         * right there when this clobbers it.
         *
         * The third branch cost the same on a first sign-in through Google or
         * LinkedIn, from the other side: with no per-user token and the store
         * not yet hydrated, `authToken` read null, so `getSession` minted a
         * BRAND NEW anonymous session and merged that empty one instead.
         *
         * Read from sessionStorage rather than the store, because at this
         * point in the bootstrap the store may not carry the token yet, which
         * is what made the third branch reachable at all. The store is kept as
         * a fallback in case the write order ever changes.
         */
        const liveToken = sessionStorage.getItem(STORAGE_KEY.TOKEN) || authToken;

        if (liveToken && liveToken !== userToken) {
          return CognitoAuthActions.cognitoCallback();
        }

        if (userToken) {
          /* Already this account's session: nothing to merge, just restore. */
          sessionStorage.setItem(STORAGE_KEY.TOKEN, userToken);
          return AuthActions.initAuthState();
        }

        /* No session at all. Mint one, then link it. */
        return CognitoAuthActions.getSession();
      })
    );
  });

  public getSession$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(CognitoAuthActions.getSession),
      switchMap(() =>
        this.authDataService.getSession().pipe(
          map(({ token, refreshToken }) => {
            this.authFacade.setTokenToStorage(token, refreshToken);
            return CognitoAuthActions.cognitoCallback();
          }),
          /*
          Without this, a failed getSession is an unhandled effect error:
          nothing is dispatched, isSessionInitializing may stay true, and the
          app hangs on the loader. Routing to getSessionFailed reuses the
          cleanup path (error page + initializing false). No loop: the session
          recovery in AuthEffects is one-shot per page load.
          */
          catchError((error) =>
            of(
              AuthActions.getSessionFailed({
                errorMessage: error?.message || 'Unknown error occurred'
              })
            )
          )
        )
      )
    );
  });

  public cognitoCallback$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(CognitoAuthActions.cognitoCallback),
      switchMap(() => this.cognitoAuthService.getIdToken()),
      switchMap((idToken) => {
        return this.cognitoAuthService.cognitoCallback(idToken).pipe(
          map((res) =>
            CognitoAuthActions.cognitoCallbackSuccess({
              token: res.token,
              refreshToken: res.refreshToken
            })
          ),
          catchError((error) =>
            of(CognitoAuthActions.cognitoCallbackFailure({ error }))
          )
        );
      })
    );
  });

  public cognitoCallbackSuccess$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(CognitoAuthActions.cognitoCallbackSuccess),
      switchMap(({ token, refreshToken }) => {
        sessionStorage.setItem(STORAGE_KEY.TOKEN, token); // save to sessionStorage for using in initAuthState

        return [
          CognitoAuthActions.saveTokenToStorage({ token, refreshToken }),
          AuthActions.initAuthState()
        ];
      })
    );
  });

  /*
  cognitoCallback failing used to dead-end: hideLoader$ hid the spinner and
  nothing else happened, leaving a silently blank page (pre-hotfix, a bogus
  /error/undefined). Route it through getSessionFailed instead: that path
  retries once via the Cognito session recovery in AuthEffects and otherwise
  cleans up dead tokens and shows the session-expired page.
  */
  public cognitoCallbackFailure$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(CognitoAuthActions.cognitoCallbackFailure),
      map(({ error }) =>
        AuthActions.getSessionFailed({
          errorMessage: error?.message || 'Cognito callback failed'
        })
      )
    );
  });

  public signOut$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CognitoAuthActions.signOut),
      withLatestFrom(this.cognitoAuthFacade.isAuthenticated$),
      switchMap(([, cognitoSignedIn]) => {
        /*
         * STAFF-3. A staff password sign-in has no Cognito session, and the
         * Cognito sign-out redirects someone who never signed in through it
         * (see deleteTokenFromStorage$). Theirs is the staff sign-out, which
         * clears the staff store and its tokens and goes home.
         */
        if (!cognitoSignedIn) {
          return [AuthActions.adminLogOut()];
        }

        this.loaderFacade.showLoaderUntil(
          CognitoAuthActions.checkAuthenticationStatus.type
        );

        return [CognitoAuthActions.signOutInProgress()];
      })
    )
  );

  public signOutSuccess$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(CognitoAuthActions.signOutInProgress),
        tap(() => {
          this.cognitoAuthService.signOut().then(() => {
            // Reset PostHog user tracking
            this.posthogService.reset();

            sessionStorage.clear();
            localStorage.clear();

            window.location.href = '/';
          });
        })
      );
    },
    { dispatch: false }
  );

  public saveTokenToStorage$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(CognitoAuthActions.saveTokenToStorage),
        withLatestFrom(this.cognitoAuthFacade.userId$),
        tap(([{ token, refreshToken }, userId]) => {
          /*
          The refresh token must be persisted unconditionally. The backend rotates
          and single-uses refresh tokens, so skipping this write leaves the browser
          holding a token the server has already invalidated, with no way back.
          It must not depend on userId, which is null for part of the magic link
          bootstrap.
          */
          if (refreshToken) {
            localStorage.setItem(STORAGE_KEY.REFRESH_TOKEN, refreshToken);
          }

          /* the per-user session token key does need a userId */
          if (token && userId) {
            localStorage.setItem(`${STORAGE_KEY.TOKEN}_${userId}`, token);
          }
        })
      );
    },
    { dispatch: false }
  );

  public deleteTokenFromStorage$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(CognitoAuthActions.deleteTokenFromStorage),
        withLatestFrom(this.cognitoAuthFacade.userId$),
        /*
        The refresh token must be removed unconditionally, mirroring the
        unconditional write in saveTokenToStorage$ above.

        This whole effect used to sit behind `filter(([, userId]) => !!userId)`.
        userId lives only in memory, so it is null on a fresh page load and
        after a session has already failed -- which is precisely when this
        action fires. The filter therefore dropped the cleanup in the one case
        that needed it, stranding a server-rejected refresh token in
        localStorage permanently. SessionInterceptor.hasSessionEvidence() then
        read that dead token as proof of a real session and sent the user to
        the 403 page on every load, browser restarts included. Clearing site
        data by hand was the only escape (CPD-652).

        The per-user token key and signOut() stay gated: the key cannot be
        built without a userId, and signOut() carries an oauth redirectUrl, so
        calling it for a user who was never signed in risks an unwanted
        redirect.
        */
        tap(([, userId]) => {
          localStorage.removeItem(STORAGE_KEY.REFRESH_TOKEN);

          if (!userId) {
            return;
          }

          localStorage.removeItem(`${STORAGE_KEY.TOKEN}_${userId}`);
          this.cognitoAuthService.signOut();
        })
      );
    },
    { dispatch: false }
  );

  /**
   * AUTH.7. Once the page knows whether this browser is already signed in,
   * offer a held link to somebody who is not. Only the first status after load
   * matters: it answers the check the landing itself dispatched.
   */
  public offerHeldMagicLink$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(
          CognitoAuthActions.checkAuthenticationStatusSuccess,
          CognitoAuthActions.checkAuthenticationStatusFailure
        ),
        take(1),
        tap((action) => {
          const isAuthenticated =
            action.type ===
              CognitoAuthActions.checkAuthenticationStatusSuccess.type &&
            !!(action as { isAuthenticated?: boolean }).isAuthenticated;
          this.magicLinkLanding?.release(isAuthenticated);
        })
      );
    },
    { dispatch: false }
  );

  public hideLoader$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(
          CognitoAuthActions.signinWithGoogleFailure,
          CognitoAuthActions.signinWithLinkedInFailure,
          CognitoAuthActions.signInWithMagicLinkFailure,
          CognitoAuthActions.confirmSignInWithMagicLinkFailure,
          CognitoAuthActions.signOutFailure,
          CognitoAuthActions.cognitoCallbackFailure,
          CognitoAuthActions.checkAuthenticationStatusFailure
        ),
        tap(() => {
          this.loaderFacade.hideLoader();
        })
      );
    },
    { dispatch: false }
  );

  constructor(
    private actions$: Actions,
    private cognitoAuthService: CognitoAuthService,
    private cognitoAuthFacade: CognitoAuthFacade,
    private loaderFacade: LoaderFacade,
    private authFacade: AuthFacade,
    private navigationService: NavigationService,
    private authDataService: AuthDataService,
    private posthogService: PosthogService,
    private location: Location,
    // AUTH.7. Trailing and optional so constructor-built specs keep compiling.
    private magicLinkLanding?: MagicLinkLandingService
  ) {}

  public ngrxOnInitEffects() {
    const pathname = window.location.pathname;
    const searchParams = new URL(window.location.href).searchParams;
    const token = searchParams.get('token');
    const email = searchParams.get('email');
    const returnToken = searchParams.get('returnToken');
    const errorDescription = searchParams.get('error_description');
    const code = searchParams.get('code');
    const state = searchParams.get('state');
    this.authFacade.clearShareToken();

    /* return process */
    if (returnToken) {
      this.navigationService.setDirectLink(
        `goals-report?returnToken=${returnToken}`
      );
      return AuthActions.processReturnSession();
    }

    if (errorDescription) {
      this.authFacade.initAuthState();
      return CognitoAuthActions.signinWithLinkedInFailure({
        error: { name: 'Error', message: errorDescription }
      });
    }

    /* magic link process */
    if (!!token && !!email) {
      /*
      The link token is single-use: left in the URL, every reload replays a
      confirmation that can never succeed again and strands the loader over
      a working session. Scrub exactly these two params before processing.
      */
      searchParams.delete('token');
      searchParams.delete('email');
      this.location.replaceState(pathname, searchParams.toString());

      /*
      AUTH.7. Hold the link instead of spending it. Mail scanners open links
      to check them, and spending on page load let the scanner sign in first.
      The link is offered once sign-in status is known (offerHeldMagicLink$)
      and spent only when a person presses "Continue to sign in".
      */
      if (this.magicLinkLanding) {
        this.magicLinkLanding.hold({ token, email });
        return CognitoAuthActions.checkAuthenticationStatus();
      }

      return CognitoAuthActions.confirmSignInWithMagicLink({ token, email });
    }

    const cognitoAuth = JSON.parse(
      sessionStorage.getItem(cognitoAuthFeatureKey)
    );
    /* click navigation back button in from google and linkedin page */
    if (cognitoAuth?.isInProgress && !code && !state) {
      return AuthActions.initAuthState();
    }

    /* account process */
    if (pathname.includes(APP_ROUTES.Account.Base)) {
      return UserActions.accountProcess();
    }

    /* check authentication status */
    return CognitoAuthActions.checkAuthenticationStatus();
  }
}
